import { ConnectedProps, connect } from 'react-redux';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Icon, MessageBarType, PrimaryButton, Stack } from '@fluentui/react';
import {
    ApiException,
    Dialog,
    LoadingSpinner,
    MessageBar,
} from '@eta/components';
import { useTranslation } from 'react-i18next';
import {
    useWizard,
    Wizard,
    WizardPivotHeader,
    WizardPivotPanel,
    WizardState,
} from '../../../common/component/WizardPivot';
import './InviteUserMain.scss';
import InviteUser from '../InviteUser';
import {
    DelegetesStatusUpdateDto,
    DelegeteStatusUpdateDto,
    LanguageTypes,
    ProfileStatus,
    ProfileTypes,
    RegisterDelegateDto,
    RepresentationInvitationDto,
    TaxpayerAccountingSystemDto,
    UserRoles,
} from '../../../Services/ClientProxy/clientProxy';
import Representation from './Representation';
import AccessBranches from './AccessBranches';
import { RepresentationContext } from './Context/RepresentationContext';
import { ServiceCreator } from '../../../Services/ClientProxy/ServiceFactory';
import { ReduxState } from '../../../common/reducers';
import { TaxpayerProfileActions } from '../../../common/config/constants';

export type InviteUserMode = 'New' | 'Edit' | 'View';

export interface InviteUserMainProps {
    hidden: boolean;
    onClose: () => void;
    language: LanguageTypes;
    onSuccessAction: (action: string) => void;
    mode?: InviteUserMode;
    representationID?: number;
    clientId?: string;
}

export enum inviteUserWizardKey {
    UserInfo = 'UserInfo',
    Represent = 'Representation',
    Branches = 'Branches',
}

const mapDispatchToProps = {};

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    currentTaxpayerProfile: state.taxpayerProfile.currentTaxpayerProfile,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const InviteUserMain: FC<InviteUserMainProps & PropsFromRedux> = (props: InviteUserMainProps & PropsFromRedux) => {
    const { onClose, hidden, language, onSuccessAction, mode, currentTaxpayerProfile, representationID, clientId } = props;
    const { t } = useTranslation(['InviteUser', 'common', 'resources']);
    const [canInvite, setCanInvite] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>();
    const [successMsg, setSuccessMsg] = useState<string>();
    const { state: currentInvitation, dispatch: dispatchInvitationRequest } =
        useContext(RepresentationContext);

    const InviteUserWizardState = (): WizardState => {
        return {
            translationNamespace: 'InviteUser',
            tabs: [
                {
                    itemKey: inviteUserWizardKey.UserInfo,
                    label: 'UserInformation',
                },
                {
                    itemKey: inviteUserWizardKey.Represent,
                    label: 'Representation',
                },
                {
                    itemKey: inviteUserWizardKey.Branches,
                    label: 'Branches',
                },
            ],
            selectedTabKey: inviteUserWizardKey.UserInfo,
            showTabState: true,
            canGoBack: false,
            canGoNext: true,
        };
    };

    const InviteUserWizard = useWizard(InviteUserWizardState());

    const setEndofDate = (activeTo: Date | undefined) => {
        // to set it with last of day.
        return activeTo
            ? new Date(
                activeTo.getFullYear(),
                activeTo.getMonth(),
                activeTo.getDate(),
                23,
                59,
                59
            )
            : undefined;
    };

    const OnInviteUserClick = async () => {
        try {
            setIsLoading(true);
            const request = new RegisterDelegateDto({
                preferredLanguage: language,
                representationInvitation: new RepresentationInvitationDto({
                    ...currentInvitation,
                    dateDeactivate: setEndofDate(
                        currentInvitation.dateDeactivate
                    ),
                }),
                userProfileType: ProfileTypes.User,
                taxpayerAccountingSystem: new TaxpayerAccountingSystemDto(),
            });
            const client = ServiceCreator.getTaxPayerProfileManagementClient();
            const response = await client.registerDelegate(request);
            if (response) {
                onClose();
                onSuccessAction(TaxpayerProfileActions.InviteUser);
            }
        } catch (ex) {
            const exception = ex as ApiException;
            setErrorMsg(
                exception?.serviceError
                    ? exception?.serviceError.toString()
                    : exception.toString()
            );
        } finally {
            setIsLoading(false);
        }
    };

    const getUserRole = (): UserRoles => {
        if (currentInvitation.hasCustomRole) return UserRoles.Custom;
        if (currentInvitation.isAdministrator) return UserRoles.Admin;
        return UserRoles.Representative;
    };

    const OnEditRepresentativeClick = async () => {
        try {
            setIsLoading(true);
            const request = new DelegetesStatusUpdateDto({
                delegeteStatusDto: [
                    new DelegeteStatusUpdateDto({
                        activeFrom: currentInvitation.dateActivate,
                        activeTo: setEndofDate(currentInvitation.dateDeactivate),
                        allBranches: currentInvitation.allBranches ?? true,
                        claims: currentInvitation.claims,
                        representationID: representationID ?? 0,
                        restrictedBranch: currentInvitation.restrictedBranch,
                        role: getUserRole(),
                        status: ProfileStatus.Active,
                        reason: undefined,
                        // activeFrom: getUTCDateFormate(
                        //     represent.dateActivated ?? undefined
                        // ),
                        // activeTo: getUTCDateFormate(
                        //     represent.dateDeactivated ?? undefined
                        // ),
                    }),
                ],
            });
            const client = ServiceCreator.getTaxPayerProfileManagementClient();
            const response = await client.updateDelegateStatus(request);
            if (response) {
                onClose();
                onSuccessAction(TaxpayerProfileActions.EditUSER);
            }
        } catch (ex) {
            const exception = ex as ApiException;
            setErrorMsg(
                exception?.serviceError
                    ? exception?.serviceError.toString()
                    : exception.toString()
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!hidden) {
            dispatchInvitationRequest({
                type: 'Reset',
            });
            setErrorMsg('');
        }
    }, [hidden]);

    useEffect(() => {
        if (mode === 'Edit' || mode === 'View') {
            const representation =
                currentTaxpayerProfile?.taxpayersRepresentation?.find(
                    (x) => x.taxpayerRepresentationID === representationID
                );
            dispatchInvitationRequest({
                type: 'updateRepresentation',
                payload: {
                    nationalID: representation?.nationalID,
                    namePrimaryLang: representation?.firstNamePrimaryLang,
                    nameSecondaryLang: representation?.firstNameSecondaryLang,
                    surnamePrimaryLang: representation?.lastNamePrimaryLang,
                    surnameSecondaryLang: representation?.lastNameSecondaryLang,
                    email: representation?.email,

                    dateActivate: representation?.dateActivated,
                    dateDeactivate: representation?.dateDeactivated,
                    hasCustomRole: representation?.hasCustomRole,
                    isAdministrator: representation?.isAdministrator,
                    claims: representation?.taxpayerRepresentationClaims?.map(
                        (x) => x.claimCode ?? ''
                    ),

                    allBranches: representation?.allBranches,
                    restrictedBranch:
                        representation?.taxpayerRepresentationBranches?.map(
                            (x) => x.branchNumber ?? -1
                        ),
                },
            });
            setErrorMsg('');
        }
    }, [mode]);

    useEffect(
        () =>
            setCanInvite(
                !InviteUserWizard.state.tabs.find(
                    (item) => item.itemState === 'error'
                ) ?? false
            ),
        [InviteUserWizard.state.selectedTabKey, InviteUserWizard.state.tabs]
    );

    return (
        <Dialog
            containerClassName="containerInviteUser"
            hidden={false}
            onDismiss={() => {
                onClose();
            }}
            maxWidth={600}
            title={(
                mode !== 'New' ? (
                    t(mode === 'View' ? 'ViewDetails' : 'editUser')
                ) : (
                    <>
                        <Icon iconName="AddFriend" /> {t('inviteNewUser')}
                    </>
                )
            )}
        >

            {successMsg && (
                <div className="errorMessage_newUser">
                    <Stack>
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            isMultiline={false}
                            className="messageBar"
                            onDismiss={() => setSuccessMsg('')}
                            truncated
                        >
                            {successMsg}
                        </MessageBar>
                    </Stack>
                </div>
            )}
            {errorMsg && (
                <div className="errorMessage_newUser">
                    <Stack>
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            className="messageBar"
                            onDismiss={() => setErrorMsg('')}
                            truncated
                        >
                            {errorMsg}
                        </MessageBar>
                    </Stack>
                </div>
            )}
            <div id="inviteUserMain">
                <Wizard state={InviteUserWizard}>
                    <div className="header">
                        <WizardPivotHeader />
                    </div>
                    <div className="content">
                        <WizardPivotPanel
                            panelKey={inviteUserWizardKey.UserInfo}
                        >
                            <InviteUser
                                InviteUserWizard={InviteUserWizard}
                                setErrorMsg={setErrorMsg}
                                setSuccessMsg={setSuccessMsg}
                                mode={mode}
                                clientId={clientId}
                            />
                        </WizardPivotPanel>
                        <WizardPivotPanel
                            panelKey={inviteUserWizardKey.Represent}
                        >
                            <Representation
                                InviteUserWizard={InviteUserWizard}
                                mode={mode}
                                readOnly={mode === 'View'}
                            />
                        </WizardPivotPanel>
                        <WizardPivotPanel
                            panelKey={inviteUserWizardKey.Branches}
                        >
                            <AccessBranches
                                InviteUserWizard={InviteUserWizard}
                                mode={mode}
                                readOnly={mode === 'View'}
                            />
                        </WizardPivotPanel>
                    </div>
                    <div className="footer">
                        {mode === 'Edit' ? (
                            <div className="navigation">
                                <PrimaryButton
                                    className="closeButton"
                                    iconProps={{ iconName: 'Cancel' }}
                                    text={t('common:cancel')}
                                    onClick={onClose}
                                />
                                <PrimaryButton
                                    menuIconProps={{ iconName: 'Save' }}
                                    disabled={!canInvite}
                                    text={t('common:save')}
                                    className="continue"
                                    onClick={() => OnEditRepresentativeClick()}
                                />
                            </div>
                        ) : (
                            <div className="navigation">
                                {InviteUserWizard.state.canGoBack && (
                                    <PrimaryButton
                                        iconProps={{ iconName: 'Back' }}
                                        disabled={
                                            !InviteUserWizard.state.canGoBack
                                        }
                                        text={t('resources:previous')}
                                        onClick={() =>
                                            InviteUserWizard.previousTab()
                                        }
                                        className="closeButton"
                                    />
                                )}
                                {InviteUserWizard.state.canGoNext ? (
                                    <PrimaryButton
                                        menuIconProps={{ iconName: 'Forward' }}
                                        disabled={
                                            !InviteUserWizard.state.canGoNext
                                        }
                                        text={t('continue')}
                                        onClick={() =>
                                            InviteUserWizard.nextTab()
                                        }
                                        className="continue"
                                    />
                                ) : (
                                    mode !== 'View' && (
                                        <PrimaryButton
                                            menuIconProps={{
                                                iconName: 'AddFriend'
                                            }}
                                            disabled={!canInvite}
                                            text={t('inviteUser')}
                                            className="continue norotate"
                                            onClick={() => OnInviteUserClick()}
                                        />
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </Wizard>
                {isLoading && (
                    <div className="loadingPanel">
                        <LoadingSpinner messageLabel={t('common:loading')} />
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default connector(InviteUserMain);
